import { useState, useEffect } from 'react';

const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(1);
  const [expandedCommitment, setExpandedCommitment] = useState<number | null>(null);
  const [activeCompanyTab, setActiveCompanyTab] = useState<'basic' | 'contact'>('basic');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const visibleElements = useScrollAnimation();

  const getAnimationClass = (elementId: string, baseClass = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategoryClick = (id: number) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  const handleCommitmentClick = (id: number) => {
    setExpandedCommitment(expandedCommitment === id ? null : id);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d56mtre9uelhi99935gg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const categories = [
    {
      id: 1,
      title: 'インテリア・雑貨',
      description: 'お部屋を心地よく彩る、ナチュラルなインテリアアイテムや日常使いの雑貨。シンプルで飽きのこないデザインが特徴です。',
      image: 'https://public.readdy.ai/ai/img_res/c44237212f1fe7a7023f91dcb2e27e81.jpg'
    },
    {
      id: 2,
      title: '日用品・消耗品',
      description: '毎日使うものだからこそ、品質と使いやすさにこだわりました。石鹸、洗剤、ケア用品など、暮らしに欠かせないアイテムを揃えています。',
      image: 'https://readdy.ai/api/search-image?query=Natural%20daily%20essentials%20and%20care%20products%20including%20organic%20soap%20bars%2C%20eco-friendly%20cleaning%20bottles%2C%20cotton%20towels%2C%20and%20bathroom%20accessories%20arranged%20on%20a%20clean%20white%20marble%20surface%20with%20soft%20natural%20lighting%2C%20minimalist%20product%20photography%20with%20beige%20and%20white%20color%20palette&width=800&height=600&seq=daily-2&orientation=landscape'
    },
    {
      id: 3,
      title: 'コスメ・スキンケア',
      description: '肌にやさしく、毎日使いたくなるスキンケアとコスメ。自然由来の成分を大切にした、安心して使える製品をお届けします。',
      image: 'https://readdy.ai/api/search-image?query=Natural%20skincare%20and%20cosmetic%20products%20including%20cream%20jars%2C%20serum%20bottles%2C%20and%20beauty%20items%20with%20minimalist%20packaging%20in%20soft%20pink%20and%20beige%20tones%2C%20arranged%20on%20a%20clean%20white%20surface%20with%20botanical%20elements%20like%20leaves%20and%20flowers%2C%20gentle%20natural%20lighting%20creating%20a%20fresh%20and%20pure%20atmosphere&width=800&height=600&seq=cosmetic-3&orientation=landscape'
    },
    {
      id: 4,
      title: '健康・ライフケア',
      description: '健やかな毎日をサポートする健康食品やライフケア商品。体の内側から、心地よい暮らしを応援します。',
      image: 'https://readdy.ai/api/search-image?query=Health%20and%20wellness%20products%20including%20supplement%20bottles%2C%20herbal%20tea%20packages%2C%20and%20natural%20health%20food%20items%20arranged%20with%20fresh%20ingredients%20like%20fruits%20and%20herbs%20on%20a%20clean%20white%20wooden%20table%2C%20bright%20natural%20lighting%20with%20green%20plants%20in%20background%2C%20promoting%20healthy%20lifestyle%20aesthetic&width=800&height=600&seq=health-4&orientation=landscape'
    }
  ];

  const commitments = [
    {
      id: 1,
      title: '使いやすさ',
      description: '毎日使うものだから、使いやすさを第一に考えています。手に馴染む形、開けやすいパッケージ、直感的に使える設計。小さな工夫が、大きな心地よさにつながります。',
      icon: 'ri-hand-heart-line'
    },
    {
      id: 2,
      title: 'デザイン',
      description: 'シンプルで飽きのこないデザインを大切にしています。どんなお部屋にも馴染み、長く愛用していただけるような、ナチュラルで洗練された見た目を追求しています。',
      icon: 'ri-palette-line'
    },
    {
      id: 3,
      title: '安心・品質',
      description: '素材選びから製造まで、品質管理を徹底しています。肌に触れるもの、口に入れるものだからこそ、安全性と品質には妥協しません。安心してお使いいただける製品をお届けします。',
      icon: 'ri-shield-check-line'
    },
    {
      id: 4,
      title: '暮らしへのなじみやすさ',
      description: '特別なものではなく、日常に自然と溶け込むアイテムを目指しています。気負わず、毎日使いたくなる。そんな「ちょうどいい」を大切にしています。',
      icon: 'ri-home-heart-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-gray-900">ターサン</div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">私たちについて</a>
              <a href="#business" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">事業内容</a>
              <a href="#products" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">商品</a>
              <a href="#commitment" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">こだわり</a>
              <a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">お問い合わせ</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/30 via-white to-white"></div>
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Bright%20and%20airy%20minimalist%20living%20room%20interior%20with%20natural%20sunlight%20streaming%20through%20large%20windows%2C%20featuring%20a%20comfortable%20beige%20linen%20sofa%20with%20soft%20cushions%2C%20potted%20green%20plants%2C%20wooden%20coffee%20table%20with%20ceramic%20items%2C%20clean%20white%20walls%2C%20and%20warm%20natural%20wood%20flooring%20creating%20a%20peaceful%20and%20inviting%20atmosphere%20perfect%20for%20daily%20life&width=1920&height=1080&seq=hero-living-1&orientation=landscape" 
            alt="Tarsan Hero" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/80"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            毎日に、<br className="md:hidden" />ちょうどいい。
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            暮らしに寄り添う、心地よいアイテムを。<br />
            Tarsanは、日常をちょっと特別にする商品をお届けします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#products" 
              className="bg-gray-900 text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              商品を見る
            </a>
            <a 
              href="https://shop.tarsan.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border-2 border-gray-900 text-gray-900 px-8 py-3 rounded-full font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center"
            >
              オンラインショップ
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        id="about" 
        data-animate 
        className={getAnimationClass('about', 'py-20 px-6')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center mb-6">
                <div className="w-2 h-2 bg-gray-900 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600 uppercase tracking-wider">About Tarsan</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                暮らしに寄り添う、<br />
                ちょうどいいを<br />
                届けたい。
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  私たちTarsanは、日常に溶け込む心地よさを大切にしています。
                </p>
                <p>
                  インテリア、日用品、コスメ、健康食品まで。毎日使うものだからこそ、使いやすさとデザイン、そして安心できる品質にこだわっています。
                </p>
                <p>
                  特別なものではなく、気負わず手に取れる。でも、ちょっとだけ暮らしが心地よくなる。そんなアイテムをお届けしたいと思っています。
                </p>
              </div>
              <div className="mt-8 flex items-center">
                <div className="font-bold text-lg">合同会社Tarsan</div>
                <div className="ml-4 flex items-center text-amber-600">
                  <i className="ri-star-fill text-sm mr-1"></i>
                  <span className="text-xs">Established 2025</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Artisan%20hands%20carefully%20arranging%20natural%20handmade%20products%20including%20ceramic%20pottery%2C%20wooden%20items%2C%20and%20organic%20skincare%20bottles%20on%20a%20clean%20white%20workspace%2C%20soft%20natural%20window%20lighting%20creating%20gentle%20shadows%2C%20warm%20and%20authentic%20craftsmanship%20atmosphere%20with%20focus%20on%20quality%20and%20attention%20to%20detail&width=800&height=1000&seq=about-craft-1&orientation=portrait" 
                alt="Tarsan Craftsmanship" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Business Section */}
      <section 
        id="business" 
        data-animate 
        className={getAnimationClass('business', 'py-20 px-6 bg-stone-50')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center mb-4">
              <div className="w-2 h-2 bg-gray-900 rounded-full mr-3"></div>
              <span className="text-sm text-gray-600 uppercase tracking-wider">What We Do</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              事業内容
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div 
              id="business-1" 
              data-animate 
              className={getAnimationClass('business-1', 'bg-rose-50 rounded-2xl overflow-hidden')}
              style={{ animationDelay: '0.1s' } as React.CSSProperties}
            >
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20minimalist%20interior%20scene%20with%20natural%20wooden%20furniture%2C%20ceramic%20vases%2C%20soft%20textile%20cushions%2C%20and%20decorative%20home%20accessories%20arranged%20on%20a%20clean%20white%20shelf%20against%20a%20light%20beige%20wall%2C%20warm%20natural%20lighting%20creating%20a%20cozy%20and%20inviting%20atmosphere&width=600&height=800&seq=business-interior-1&orientation=portrait" 
                  alt="インテリア・雑貨" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold mb-2">インテリア・雑貨</h3>
                <p className="text-sm text-gray-600">お部屋を心地よく彩る家具や雑貨、服飾小物の製造・販売</p>
              </div>
            </div>

            <div 
              id="business-2" 
              data-animate 
              className={getAnimationClass('business-2', 'bg-emerald-50 rounded-2xl overflow-hidden')}
              style={{ animationDelay: '0.2s' } as React.CSSProperties}
            >
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Natural%20organic%20soap%20bars%2C%20eco-friendly%20cleaning%20product%20bottles%2C%20and%20daily%20care%20items%20arranged%20neatly%20on%20a%20clean%20white%20surface%20with%20green%20plant%20leaves%2C%20bright%20natural%20lighting%20emphasizing%20purity%20and%20cleanliness%2C%20minimalist%20product%20photography%20with%20fresh%20and%20clean%20aesthetic&width=600&height=800&seq=business-daily-2&orientation=portrait" 
                  alt="日用品・ケア用品" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold mb-2">日用品・ケア用品</h3>
                <p className="text-sm text-gray-600">石鹸、洗剤、医薬部外品など、毎日使うケアアイテム</p>
              </div>
            </div>

            <div 
              id="business-3" 
              data-animate 
              className={getAnimationClass('business-3', 'bg-amber-50 rounded-2xl overflow-hidden')}
              style={{ animationDelay: '0.3s' } as React.CSSProperties}
            >
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Elegant%20cosmetic%20and%20skincare%20product%20collection%20including%20cream%20jars%2C%20serum%20bottles%2C%20and%20beauty%20items%20with%20minimalist%20beige%20and%20white%20packaging%2C%20arranged%20artistically%20with%20dried%20flowers%20and%20natural%20elements%20on%20a%20soft%20pink%20background%2C%20luxurious%20yet%20natural%20beauty%20product%20photography&width=600&height=800&seq=business-cosmetic-3&orientation=portrait" 
                  alt="コスメ・スキンケア" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold mb-2">コスメ・スキンケア</h3>
                <p className="text-sm text-gray-600">肌にやさしい化粧品の製造・販売・輸出入</p>
              </div>
            </div>

            <div 
              id="business-4" 
              data-animate 
              className={getAnimationClass('business-4', 'bg-slate-50 rounded-2xl overflow-hidden')}
              style={{ animationDelay: '0.4s' } as React.CSSProperties}
            >
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20e-commerce%20workspace%20showing%20laptop%20computer%20displaying%20online%20shopping%20website%2C%20smartphone%20with%20shopping%20app%2C%20product%20packages%20ready%20for%20shipping%2C%20and%20small%20business%20owner%20working%20at%20clean%20white%20desk%2C%20bright%20natural%20lighting%20creating%20professional%20and%20efficient%20atmosphere&width=600&height=800&seq=business-ec-4&orientation=portrait" 
                  alt="EC・通販事業" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold mb-2">EC・通販事業</h3>
                <p className="text-sm text-gray-600">インターネットを通じた便利なお買い物体験を提供</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section 
        id="products" 
        data-animate 
        className={getAnimationClass('products', 'py-20 px-6')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center mb-4">
              <div className="w-2 h-2 bg-gray-900 rounded-full mr-3"></div>
              <span className="text-sm text-gray-600 uppercase tracking-wider">Products</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 flex items-center justify-center">
              商品カテゴリー
              <span className="ml-3 text-4xl">🛋️</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <div
                key={category.id}
                id={`category-${category.id}`}
                data-animate
                className={getAnimationClass(`category-${category.id}`, `relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                  expandedCategory === category.id ? 'md:col-span-2' : ''
                }`)}
                style={{ animationDelay: `${(index + 1) * 0.1}s` } as React.CSSProperties}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className={`relative ${expandedCategory === category.id ? 'h-96' : 'h-80'} transition-all duration-500`}>
                  <img 
                    src={category.image} 
                    alt={category.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-2xl font-bold mb-2">{category.title}</h3>
                    <div className={`transition-all duration-500 overflow-hidden ${
                      expandedCategory === category.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <p className="text-sm leading-relaxed text-white/90 mt-2">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://shop.tarsan.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gray-900 text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              オンラインショップで全商品を見る
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section 
        id="commitment" 
        data-animate 
        className={getAnimationClass('commitment', 'py-20 px-6 bg-stone-50')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center mb-6">
                <div className="w-2 h-2 bg-gray-900 rounded-full mr-3"></div>
                <span className="text-sm text-gray-600 uppercase tracking-wider">Our Philosophy</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                使いやすさと、<br />
                美しさと、<br />
                安心を。
              </h2>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20carefully%20inspecting%20natural%20handmade%20product%20quality%2C%20artisan%20checking%20ceramic%20pottery%20texture%20and%20finish%2C%20soft%20natural%20lighting%20highlighting%20craftsmanship%20details%2C%20warm%20and%20authentic%20atmosphere%20emphasizing%20quality%20control%20and%20attention%20to%20detail%20in%20product%20creation&width=800&height=600&seq=commitment-quality-1&orientation=landscape" 
                alt="Tarsan Quality" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {commitments.map((commitment, index) => (
              <div
                key={commitment.id}
                id={`commitment-${commitment.id}`}
                data-animate
                className={getAnimationClass(`commitment-${commitment.id}`, `bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${
                  expandedCommitment === commitment.id ? 'border-gray-900' : 'border-transparent'
                }`)}
                style={{ animationDelay: `${(index + 1) * 0.1}s` } as React.CSSProperties}
                onClick={() => handleCommitmentClick(commitment.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${commitment.icon} text-2xl text-gray-900`}></i>
                  </div>
                  <i className={`ri-arrow-right-up-line text-xl text-gray-400 transition-transform duration-300 ${
                    expandedCommitment === commitment.id ? 'rotate-45' : ''
                  }`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{commitment.title}</h3>
                <div className={`transition-all duration-500 overflow-hidden ${
                  expandedCommitment === commitment.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {commitment.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-xl">
            <div className="relative h-96 md:h-auto">
              <img 
                src="https://readdy.ai/api/search-image?query=Peaceful%20morning%20scene%20of%20person%20enjoying%20coffee%20while%20browsing%20online%20shop%20on%20tablet%20in%20cozy%20minimalist%20living%20room%2C%20natural%20sunlight%20streaming%20through%20window%2C%20comfortable%20beige%20sofa%20with%20soft%20cushions%2C%20potted%20plants%20nearby%2C%20warm%20and%20inviting%20atmosphere%20representing%20comfortable%20daily%20life%20and%20online%20shopping%20experience&width=800&height=800&seq=cta-lifestyle-1&orientation=squarish" 
                alt="Online Shopping" 
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 text-xs text-white/80 uppercase tracking-wider">Online Shop</div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  いつもの暮らしに、<br />
                  新しい心地よさを。
                </h3>
              </div>
            </div>
            <div className="bg-white p-12 flex flex-col justify-center">
              <h3 className="text-2xl font-bold mb-4 leading-tight">
                オンラインショップで<br />
                全商品をご覧いただけます
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Tarsanの商品は、すべてオンラインショップでお求めいただけます。日用品からインテリア、コスメまで、暮らしを彩るアイテムが揃っています。
              </p>
              <a 
                href="https://shop.tarsan.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-gray-900 text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                ショップを見る
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact" 
        data-animate 
        className={getAnimationClass('contact', 'py-20 px-6 bg-stone-50')}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center mb-4">
              <div className="w-2 h-2 bg-gray-900 rounded-full mr-3"></div>
              <span className="text-sm text-gray-600 uppercase tracking-wider">Contact</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">お問い合わせ</h2>
            <p className="text-gray-600">ご質問やご相談がございましたら、お気軽にお問い合わせください。</p>
          </div>

          <form 
            id="contact-form"
            data-readdy-form
            onSubmit={handleFormSubmit} 
            className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  placeholder="山田 太郎"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  placeholder="example@email.com"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                placeholder="090-1234-5678"
              />
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none text-sm"
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500文字
              </div>
            </div>
            <button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="w-full bg-gray-900 text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'submitting' ? '送信中...' : '送信する'}
            </button>
            {formStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm text-center">
                お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
              </div>
            )}
            {formStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm text-center">
                送信に失敗しました。もう一度お試しください。
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="text-3xl font-bold mb-6">ターサン</div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                暮らしに寄り添う、心地よいアイテムを。日常をちょっと特別にする商品をお届けします。
              </p>
              <div className="flex space-x-4">
                <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                </a>
                <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="https://line.me/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                  <i className="ri-line-line"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-4">会社情報</h4>
              
              {/* Tab Buttons */}
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setActiveCompanyTab('basic')}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCompanyTab === 'basic'
                      ? 'bg-white text-gray-900'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  基本情報
                </button>
                <button
                  onClick={() => setActiveCompanyTab('contact')}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCompanyTab === 'contact'
                      ? 'bg-white text-gray-900'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  連絡先
                </button>
              </div>

              {/* Tab Content */}
              <div className="space-y-3 text-sm text-gray-400">
                {activeCompanyTab === 'basic' ? (
                  <>
                    <div>
                      <div className="font-medium text-white mb-1">正式名称</div>
                      <div>合同会社Tarsan</div>
                    </div>
                    <div>
                      <div className="font-medium text-white mb-1">設立月日</div>
                      <div>2019年5月30日</div>
                    </div>
                    <div>
                      <div className="font-medium text-white mb-1">資本金</div>
                      <div>50万円</div>
                    </div>
                    <div>
                      <div className="font-medium text-white mb-1">代表社員</div>
                      <div>細川貴弘</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="font-medium text-white mb-1">住所</div>
                      <div>大阪府大阪市中央区東心斎橋1-17-5-403</div>
                    </div>
                    <div>
                      <div className="font-medium text-white mb-1">電話番号</div>
                      <a href="tel:+81501784563" className="hover:text-white transition-colors cursor-pointer">050-1784-5633</a>
                    </div>
                    <div>
                      <div className="font-medium text-white mb-1">メール</div>
                      <a href="mailto:tarsan0530@gmail.com" className="hover:text-white transition-colors cursor-pointer">tarsan0530@gmail.com</a>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-4">リンク</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <a href="#about" className="block hover:text-white transition-colors cursor-pointer">私たちについて</a>
                <a href="#business" className="block hover:text-white transition-colors cursor-pointer">事業内容</a>
                <a href="#products" className="block hover:text-white transition-colors cursor-pointer">商品カテゴリー</a>
                <a href="#commitment" className="block hover:text-white transition-colors cursor-pointer">こだわり</a>
                <a href="#contact" className="block hover:text-white transition-colors cursor-pointer">お問い合わせ</a>
                <a href="/privacy" className="block hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <div className="mb-4 md:mb-0">
              © 2025 Tarsan LLC. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <a href="/privacy" className="hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
              <a href="/terms" className="hover:text-white transition-colors cursor-pointer">利用規約</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
